import {
  buildAnnotateAction,
  buildExportAction,
  buildImportAction,
  buildManageAction,
  buildScanActionOptions,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { getAssetScanOptions } from '@/sections/asset/config/actionOptions';
import { useAssetActionHandlers } from '@/sections/asset/hooks/actions';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { Asset, AssetStatus } from '@/types';

interface AssetActionsProps {
  /** Assets to perform actions on */
  assets: Asset[];
  /** Callback for successful actions (legacy, unused) */
  onSuccess?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Handler for opening tag modal */
  onTagClick?: () => void;
  /** Handler for opening comment modal */
  onCommentClick?: () => void;
  /** Handler for opening remove tag modal */
  onRemoveTagClick?: () => void;
  /** Handler for opening import modal (always enabled) */
  onImportClick?: () => void;
  /** Handler for opening export modal (always enabled) */
  onExportClick: () => void;
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Disable actions when no items selected */
  disabled?: boolean;
}

export function AssetActions({
  assets,
  className,
  hideLabel,
  onCommentClick,
  onTagClick,
  onRemoveTagClick,
  onImportClick,
  onExportClick,
  hideActions,
  disabled = false,
}: AssetActionsProps) {
  const handlers = useAssetActionHandlers();
  const {
    modal: { selectUserAgent },
  } = useGlobalState();
  const { isPraetorianUser } = useAuth();

  // Build dropdown options using option builders
  const scanOptions = getAssetScanOptions({
    assets,
    handlers,
    selectUserAgent,
    isPraetorianUser,
  });

  // Build action configurations
  const actions = [
    buildAnnotateAction({
      onCommentClick: onCommentClick || (() => {}),
      onTagClick: onTagClick || (() => {}),
      onRemoveTagClick: onRemoveTagClick || (() => {}),
      labels: { comment: 'Add Note' },
      testId: 'asset-annotate-dropdown',
    }),

    buildScanActionOptions({
      scanOptions,
      testId: 'asset-scan-dropdown',
    }),

    buildShareAction({
      variant: 'button-only',
      onShareClick: () => handlers.handleShare(assets),
      testId: 'asset-share-button',
    }),

    buildManageAction({
      onFreezeClick: () =>
        handlers.handleUpdateStatus(assets, AssetStatus.Frozen),
      onActivateClick: () =>
        handlers.handleUpdateStatus(assets, AssetStatus.Active),
      onDeleteClick: () =>
        handlers.handleUpdateStatus(assets, AssetStatus.Deleted),
      shortcuts: {
        freeze: ' ⌥ F',
        activate: ' ⌥ U',
        delete: ' ⌥ D',
      },
      // Non-Praetorian users cannot unfreeze assets (backend enforces this)
      hideActions: { activate: !isPraetorianUser },
      testId: 'asset-managed-dropdown',
    }),

    ...(onImportClick
      ? [
          buildImportAction({
            onImportClick,
            disabled: false, // Always enabled
            testId: 'asset-import-button',
          }),
        ]
      : []),

    buildExportAction({
      onExportClick,
      disabled: false, // Always enabled
      testId: 'asset-export-button',
    }),
  ];

  return (
    <TableActionsBar
      selectedItems={assets}
      actions={actions}
      disabled={disabled}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="asset"
    />
  );
}
